// Tenantra Inc. - Services (Add-ons)
// A scalable list of optional services the landlord can add or remove.
import { useState, useEffect } from 'react';
import { ShieldCheck, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TableSkeleton } from '@/components/dashboard/TableKit';
import api from '@/services/api';
import { toast } from 'sonner';

function ServiceRow({ service, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const active = service.active;

  const act = async (verb) => {
    setBusy(true);
    try {
      await api.post(`/services/${service.key}/${verb}`);
      toast.success(verb === 'add' ? `${service.name} added` : `${service.name} removed`);
      setConfirmRemove(false);
      await onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  const Icon = service.icon;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`service-${service.key}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-9 h-9 rounded-md bg-teal-600 flex items-center justify-center flex-shrink-0">
            <Icon className="w-[18px] h-[18px] text-white" strokeWidth={1.75} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-[15px] font-semibold text-slate-900">{service.name}</h3>
              {active ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-teal-50 text-teal-700" data-testid={`service-${service.key}-active`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-500" /> Active
                </span>
              ) : (
                <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-500" data-testid={`service-${service.key}-inactive`}>Not added</span>
              )}
            </div>
            <p className="text-sm text-slate-500 mt-0.5">{service.description}</p>
          </div>
        </div>

        <div className="flex-shrink-0">
          {active ? (
            confirmRemove ? (
              <div className="flex items-center gap-2">
                <Button variant="ghost" className="h-8 px-3 text-sm text-slate-600" onClick={() => setConfirmRemove(false)} disabled={busy} data-testid={`service-${service.key}-cancel`}>Cancel</Button>
                <Button className="h-8 px-3 text-sm bg-red-600 hover:bg-red-700 text-white" onClick={() => act('remove')} disabled={busy} data-testid={`service-${service.key}-confirm-remove`}>
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Remove'}
                </Button>
              </div>
            ) : (
              <Button variant="outline" className="h-8 px-4 text-sm border-slate-300 text-slate-700 hover:border-red-300 hover:text-red-600" onClick={() => setConfirmRemove(true)} data-testid={`service-${service.key}-remove`}>Remove</Button>
            )
          ) : (
            <Button className="h-8 px-5 text-sm bg-teal-600 hover:bg-teal-700 text-white" onClick={() => act('add')} disabled={busy || !service.canAdd} data-testid={`service-${service.key}-add`}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Add'}
            </Button>
          )}
        </div>
      </div>

      {/* Pricing + usage strip */}
      <div className="flex flex-wrap items-center gap-x-8 gap-y-3 mt-4 pt-4 border-t border-slate-100">
        {service.prices.map((p) => (
          <div key={p.label}>
            <div className="flex items-baseline gap-1">
              <span className="text-lg font-semibold text-slate-900">{p.value}</span>
              <span className="text-[13px] text-slate-500">{p.unit}</span>
            </div>
            <p className="text-[11px] text-slate-400 mt-0.5">{p.note}</p>
          </div>
        ))}
        {active && service.usage && (
          <div className="ml-auto text-right" data-testid={`service-${service.key}-usage`}>
            <div className="text-lg font-semibold text-slate-900">{service.usage.value}</div>
            <p className="text-[11px] text-slate-400 mt-0.5">{service.usage.note}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ServicesPage() {
  const [loading, setLoading] = useState(true);
  const [screening, setScreening] = useState(null);

  const fetchData = async () => {
    try {
      const res = await api.get('/services/screening');
      setScreening(res.data);
    } catch {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) return <TableSkeleton rows={4} spans={[5, 3, 2, 2]} />;

  const services = [
    {
      key: 'screening',
      name: 'Tenant Screening',
      icon: ShieldCheck,
      description: 'Every application is screened the moment it arrives, so you never vet an applicant by hand.',
      active: !!screening?.active,
      canAdd: !!screening?.has_plan,
      prices: [
        { label: 'monthly', value: `$${screening?.monthly_price?.toFixed(2)}`, unit: '/mo', note: 'Billed in advance' },
        { label: 'per_app', value: `$${screening?.per_application_price?.toFixed(2)}`, unit: 'per applicant', note: 'Re-runs are free' },
      ],
      usage: {
        value: `$${screening?.current_period_usage_amount?.toFixed(2)}`,
        note: `${screening?.current_period_usage_count || 0} screened this period`,
      },
    },
  ];

  return (
    <div className="max-w-3xl space-y-4" data-testid="services-page">
      <div>
        <h1 className="text-lg font-bold text-slate-900">Services</h1>
        <p className="text-sm text-slate-500 mt-0.5">Optional services you can add or remove. Everything bills on your regular plan invoice.</p>
      </div>

      {!screening?.has_plan && (
        <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3" data-testid="services-no-plan">
          <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-800">You need an active plan before adding services. Head to Billing to choose one.</p>
        </div>
      )}

      <div className="space-y-3">
        {services.map((s) => (
          <ServiceRow key={s.key} service={s} onChanged={fetchData} />
        ))}
      </div>
    </div>
  );
}
